import React, { useEffect, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { getStoredToken } from '../../utils/authStorage';

interface RichTextEditorProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    label?: string;
    minHeight?: string;
    folder?: string;
}

declare global {
    interface Window {
        Quill: any;
    }
}

const sanitizeHtml = (html: string) =>
    DOMPurify.sanitize(html, {
        USE_PROFILES: { html: true },
    });

const RichTextEditor: React.FC<RichTextEditorProps> = ({
    value,
    onChange,
    placeholder = 'Start typing...',
    label,
    minHeight = '200px',
    folder = 'editor'
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const quillInstance = useRef<any>(null);
    const [isQuillLoaded, setIsQuillLoaded] = useState(false);
    const isInternallyUpdating = useRef(false);
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        if (!document.getElementById('quill-css')) {
            const link = document.createElement('link');
            link.id = 'quill-css';
            link.rel = 'stylesheet';
            link.href = '/vendor/quill/quill.snow.css';
            document.head.appendChild(link);
        }

        if (!window.Quill) {
            const script = document.createElement('script');
            script.id = 'quill-script';
            script.src = '/vendor/quill/quill.js';
            script.onload = () => setIsQuillLoaded(true);
            document.head.appendChild(script);
        } else {
            setIsQuillLoaded(true);
        }
    }, []);

    const selectLocalImage = () => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files ? input.files[0] : null;
            if (file) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('folder', folder);

                try {
                    const baseURL = import.meta.env.VITE_API_BASE || 'http://localhost:8000';
                    const token = getStoredToken();

                    const response = await fetch(`${baseURL}/api/media/file/upload`, {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'Authorization': token ? `Bearer ${token}` : ''
                        }
                    });

                    const result = await response.json();
                    if (result.data && result.data.path) {
                        const url = result.data.path.startsWith('http')
                            ? result.data.path
                            : `${baseURL}/${result.data.path}`;
                        const range = quillInstance.current?.getSelection();
                        if (range && quillInstance.current) {
                            quillInstance.current.insertEmbed(range.index, 'image', url);
                        } else if (quillInstance.current) {
                            const length = quillInstance.current.getLength();
                            quillInstance.current.insertEmbed(length, 'image', url);
                        }
                    } else {
                        throw new Error('Upload failed');
                    }
                } catch (error) {
                    console.error('Image upload failed:', error);
                    alert('Image upload failed');
                }
            }
        };
    };

    useEffect(() => {
        if (isQuillLoaded && editorRef.current && !quillInstance.current) {
            quillInstance.current = new window.Quill(editorRef.current, {
                theme: 'snow',
                placeholder,
                modules: {
                    toolbar: {
                        container: [
                            [{ 'header': [1, 2, 3, false] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            [{ 'color': [] }, { 'background': [] }],
                            ['link', 'image', 'video'],
                            ['clean']
                        ],
                        handlers: {
                            image: selectLocalImage
                        }
                    }
                }
            });

            if (value) {
                quillInstance.current.root.innerHTML = sanitizeHtml(value);
            }

            quillInstance.current.on('text-change', () => {
                if (!isInternallyUpdating.current && quillInstance.current) {
                    const html = quillInstance.current.root.innerHTML;
                    const sanitizedHtml = html === '<p><br></p>' ? '' : sanitizeHtml(html);
                    onChangeRef.current(sanitizedHtml);
                }
            });
        }
    }, [isQuillLoaded, placeholder, folder]);

    useEffect(() => {
        if (quillInstance.current && value !== quillInstance.current.root.innerHTML) {
            isInternallyUpdating.current = true;
            quillInstance.current.root.innerHTML = sanitizeHtml(value || '');
            isInternallyUpdating.current = false;
        }
    }, [value]);

    return (
        <div className="rich-text-editor-container">
            {label && <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">{label}</label>}
            <div
                className="bg-white dark:bg-gray-950 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700"
                style={{ minHeight }}
            >
                <div ref={editorRef} style={{ height: 'auto', border: 'none' }} />
            </div>
            <style>{`
                .ql-toolbar.ql-snow {
                    border: none !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    background: #f9fafb;
                }
                .dark .ql-toolbar.ql-snow {
                    background: #111827;
                    border-bottom: 1px solid #374151 !important;
                }
                .ql-container.ql-snow {
                    border: none !important;
                    font-family: inherit;
                    font-size: 0.875rem;
                }
                .ql-editor {
                    min-height: ${minHeight};
                    color: inherit;
                }
                .dark .ql-editor {
                    color: #d1d5db;
                }
                .dark .ql-snow .ql-stroke {
                    stroke: #9ca3af;
                }
                .dark .ql-snow .ql-fill {
                    fill: #9ca3af;
                }
                .dark .ql-snow .ql-picker {
                    color: #9ca3af;
                }
                .dark .ql-snow .ql-picker-options {
                    background-color: #1f2937;
                    border: 1px solid #374151;
                    color: #d1d5db;
                }
            `}</style>
        </div>
    );
};

export default RichTextEditor;
